<template>
    <div spellcheck="false">
        <v-layout v-if="showLabel && (itemList.length > 0 || isEditable)" style="margin-bottom: 3px">
            <div class="label mr-6" :style="{ width: isEditable ? '178px' : '197px' }">{{ keyLabel | capitalize }}</div>
            <div class="label">{{ valueLabel | capitalize }}</div>
        </v-layout>
        <div
            v-if="!isEditable && itemList.length === 0"
            style="font-family: 'open sans',serif; font-size: 14px; color: #727885"
        >
            {{ $f.tc('noDataAvailable') }}
        </div>
        <v-layout v-for="(item, i) in itemList" :key="`api_parameter_${i}`" :class="i === 0 ? '' : 'mt-8'">
            <ScenarioSelect v-if="keyList" :items="keyList" v-model="item.key" class="param-key mr-6"
                            :editable="editable"></ScenarioSelect>
            <v-text-field
                v-else
                hide-details
                v-model="item.key"
                class="param-key input-custom mr-6"
                ref="ScenarioInput"
                :readonly="!editable"
                @focusout="save"
                @keydown.enter="save"
            ></v-text-field>
            <v-text-field
                hide-details
                v-model="item.value"
                class="param-value input-custom"
                ref="ScenarioInput"
                :readonly="!editable"
                @focusout="save"
                @keydown.enter="save"
            ></v-text-field>
            <div class="btn-icon-2 ml-6" v-if="isEditable">
                <img
                    v-if="editable"
                    src="/img/scenario/delete-new_red.svg"
                    alt="icon-delete"
                    style="width: 18px;"
                    @click="removeItem(i)"
                >
            </div>
        </v-layout>
        <v-layout align-center v-if="isEditable" class="mt-8">
            <ScenarioSelect
                v-model="key"
                class="param-key mr-6"
                ref="KeyEditor"
                :items="keyList"
                v-if="keyList"
                :placeholder="$f.tc('enterKey')"
            ></ScenarioSelect>
            <v-text-field
                v-else
                hide-details
                v-model="key"
                class="param-key input-custom mr-6"
                ref="KeyEditor"
                :placeholder="$f.tc('enterKey')"
                :readonly="!editable"
                @keypress.enter="focusValueInput"
            ></v-text-field>
            <v-text-field
                hide-details
                v-model="value"
                class="param-value input-custom mr-6"
                ref="ValueEditor"
                :placeholder="$f.tc('enterValue')"
                :readonly="!editable"
                @keypress.enter="addItem"
            ></v-text-field>
            <div class="btn-icon-2">
                <img
                    v-if="allowAddItem"
                    src="/img/scenario/add_grey.svg"
                    alt="icon-add-param"
                    style="width: 16px"
                    @click="addItem"
                >
            </div>
        </v-layout>
        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
    </div>
</template>

<script>
import ScenarioSelect from "@/views/Scenario/components/ScenarioSelect";
import ScenarioService from "@/services/scenario.service";

export default {
    name: "ApiParameterEditor",
    components: {ScenarioSelect},
    props: {
        items: {
            type: [Object, Array]
        },
        keyLabel: {
            type: String,
            default: "Key"
        },
        valueLabel: {
            type: String,
            default: "Value"
        },
        showLabel: {
            type: Boolean,
            default: true
        },
        dataFormat: {
            type: String,
            default: 'object'
        },
        editable: {
            type: Boolean,
            default: true
        },
        keyList: Array,
        allowAddItem: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            itemList: [],
            key: '',
            value: '',
            errorMessage: '',
            listener: null
        }
    },
    computed: {
        isEditable() {
            if (typeof this.editable === 'boolean') {
                return this.editable
            }
            return ScenarioService.isEditable() && ScenarioService.isModeEditing()
        }
    },
    methods: {
        updateItemList() {
            if (Array.isArray(this.items)) {
                this.itemList = JSON.parse(JSON.stringify(this.items))
            } else {
                let list = []
                for (let i in this.items) {
                    list.push({
                        key: i,
                        value: this.items[i]
                    })
                }
                this.itemList = list
            }
        },
        focusValueInput () {
            this.$nextTick(() => {
                this.$refs.ValueEditor.$refs.input.focus()
            })
        },
        removeItem(index) {
            this.itemList.splice(index, 1)
            this.save()
        },
        addItem() {
            this.checkLastItem()
            if (!this.errorMessage) {
                this.itemList.push({
                    key: this.key,
                    value: this.value
                })
                this.key = ''
                this.value = ''
                this.$refs.KeyEditor.$refs.input.focus()
            }
            this.save()
        },
        checkLastItem() {
            if (this.key) {
                let item = this.itemList.find(item => item.key === this.key)
                if (item) {
                    this.errorMessage = `key duplicate - ${this.key}`
                } else {
                    this.errorMessage = ''
                }
            } else {
                this.errorMessage = (this.$f.tc(this.keyLabel) || 'Key') + ' field is required'
            }
        },
        save() {
            let valueOut = null
            if (this.dataFormat === 'object') {
                valueOut = {}
                for (const element of this.itemList) {
                    element.key = element.key.trim()
                    let item = element
                    if (!item.key) {
                        this.$appService.toastError((this.$f.tc(this.keyLabel) || 'Key') + ' field is required')
                        this.updateItemList()
                        return
                    }
                    valueOut[item.key] = item.value
                }
            } else {
                valueOut = this.itemList
            }

            if (JSON.stringify(valueOut) !== JSON.stringify(this.items)) {
                this.$emit('update:items', valueOut)
                this.$emit('save')
            }
        },
    },
    watch: {
        items: {
            immediate: true,
            handler: 'updateItemList'
        }
    }
}
</script>

<style scoped lang="scss">
.param-key {
    width: 149px;
}

.param-value {
    flex-grow: 1;
}

.error-message {
    color: red;
    margin-top: 5px;
    font-size: 13px;
    font-family: "open sans",serif;
}

img {
    width: 16px;
    flex-shrink: 0;
    cursor: pointer;
}

.label {
    font-family: "open sans semi",serif !important;
    font-size: 13px;
    margin-bottom: 0 !important;
}

.input-custom {
    ::v-deep {
        .v-input__slot {
            height: 32px;
            background: #FFFFFF;
            border-radius: 5px;
            border: 1px solid #D9D9D9;
            outline: none;
            font-size: 14px;
            padding: 6px 12px 6px 12px;
            margin: 0;
            display: flex;
            align-items: center;

            &:before, &:after {
                border: none !important;
            }
        }
    }
}

::v-deep {
    .v-text-field {
        padding-top: 0 !important;
        margin-top: 0 !important;
    }

    .v-text-field input {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
}
</style>
